import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, from, switchMap, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { TokenRefreshHandler } from '../../infrastructure/authentication/token-refresh-handler.base-service';
import { AuthTokenService } from '../../infrastructure/authentication/auth-token.base-service';

const TOKEN_REFRESHED_HEADER = 'X-Token-Refreshed';
const GRAPHQL_AUTH_ERROR_CODES = ['UNAUTHENTICATED', 'FORBIDDEN'];

@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  private tokenRefreshHandler = inject(TokenRefreshHandler);
  private authTokenService = inject(AuthTokenService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private hasGraphQLAuthError(body: any): boolean {
    if (!body?.errors || !Array.isArray(body.errors)) {
      return false;
    }
    return body.errors.some(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (err: any) =>
        GRAPHQL_AUTH_ERROR_CODES.includes(err?.extensions?.code) ||
        GRAPHQL_AUTH_ERROR_CODES.includes(err?.extensions?.classification)
    );
  }

  private handleUnauthorized(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.tokenRefreshHandler.isRefreshing()) {
      return this.tokenRefreshHandler.onRefreshComplete().pipe(
        switchMap(() => this.retryRequest(request, next)),
        catchError(err => throwError(() => err))
      );
    }

    return from(this.tokenRefreshHandler.refreshAccessToken()).pipe(
      switchMap(() => this.retryRequest(request, next)),
      catchError(err => throwError(() => err))
    );
  }

  private retryRequest(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const token = this.authTokenService.getToken();
    const clonedRequest = request.clone({
      setHeaders: {
        Authorization: `Bearer ${String(token?.accessToken)}`,
        [TOKEN_REFRESHED_HEADER]: 'true',
      },
    });
    return next.handle(clonedRequest);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isGraphQL = request.url.indexOf('/graphql') > -1;
    const alreadyRetried = request.headers.has(TOKEN_REFRESHED_HEADER);

    return next.handle(request).pipe(
      // #19: Handle GraphQL auth errors returned as 200 with error body
      map(event => {
        if (
          isGraphQL &&
          !alreadyRetried &&
          event instanceof HttpResponse &&
          this.hasGraphQLAuthError(event.body)
        ) {
          throw new HttpErrorResponse({
            status: 401,
            statusText: 'GraphQL UNAUTHENTICATED',
            url: request.url,
          });
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && isGraphQL && !alreadyRetried) {
          return this.handleUnauthorized(request, next);
        }

        return throwError(() => error);
      })
    );
  }
}
